import type { ReactElement } from "react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Chip, FormControlLabel, Switch, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { NavigateFunction } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { v4 } from "uuid";

import type {
  ExtendedThalamosUser,
  ExternalPatientLinkEnhanced,
  FormContextData,
  FormContextMiniForm,
  PatientTimelineResponse,
  Section12PaymentRequestSummary,
  UUID,
} from "@thalamos/aspire-common";
import {
  canAmhpSearchS12Doctors,
  canAmhpTeamApproveS12Payments,
  findFormsMerged,
  formIsApplication,
  formIsMedRec,
  formIsRecordOfDetention,
  formIsSingleMedRec,
  getBaseFormTemplate,
  getFullFormName,
  getLatestVersion,
  hasDoctorGmcNumber,
  isExtendedTeamMembership,
  isGuestUserSession,
  isIndependentUserSession,
} from "@thalamos/aspire-common";

import { FormCategoryRow } from "../../../components/FormCategoryRow.js";
import { LoggedInUserContext } from "../../../Contexts.js";
import { useScreenDetection } from "../../../hooks/ScreenDetection/useScreenDetection.js";
import { routeFns } from "../../../routePaths.js";
import { AdmissionPatientDemographicsOutdatedBanner } from "../../FormDraft/helpers/comparePatientDemographics.js";
import { NewFormDialog } from "../../NewFormDialog.js";
import {
  formatDoctorDisplayName,
  STATUS_CHIP_SX,
  STATUS_COLORS,
  STATUS_LABELS,
} from "../../Section12PaymentRequests/payment-request-helpers.js";
import {
  admissionFormContextLogic,
  canContinueAdmissionForm,
  canStartAdmissionForm,
  userSignsNextReplacementPart,
} from "../helpers/formContextLogic.js";
import { VersionFormsPdfViewer } from "../helpers/FormContextPdfViewer.js";
import { ParticipantsList } from "../helpers/ParticipantsList.js";
import { Section4Banners } from "../helpers/Section4Banners.js";
import {
  hasNoActiveWorkItemOrRejectedCancelledOrCompleted,
  hasNoActiveWorkItemOrRejectedOrCancelled,
  isGuestDoctorForFormContext,
} from "../helpers/workItemHelpers.js";
import { WorkItemStatusCard } from "../helpers/WorkItemStatusCard.js";
import { AdmissionFormRow } from "./AdmissionFormRow.js";
import { AmhpPaymentRequestReviewPopup } from "./AmhpPaymentRequestReviewPopup.js";
import { DoctorPaymentRequestsSection } from "./DoctorPaymentRequestsSection.js";
import {
  createDraftAndHandleResult,
  findResumableReplacementDraftId,
} from "./helpers/index.js";
import { DoctorInterviewsSection } from "./InterviewManagement/DoctorInterviewsSection.js";
import { InterviewsSection } from "./InterviewManagement/InterviewsSection.js";
import {
  Banner,
  BannerList,
  Button,
  ButtonIcon,
  FormTitle,
  PopupDialog,
  PopupDialogTitle,
} from "~/components/design-system/index.js";
import { FormFooterSection } from "~/components/form/index.js";
import { PatientBanner } from "~/components/layout/index.js";
import { Container, ExpandMore } from "~/components/layout/styleWrappers.js";
import { SelectFormsDialog } from "~/hooks/ExternalPatientLink/SelectFormsUploadDialog.js";
import { useExternalPatientLinks } from "~/hooks/ExternalPatientLink/useExternalPatientLinks.js";
import { userCanEditMhaStatus } from "~/pages/EditMhaStatus/userCanEditMhaStatus.js";

function newForms({
  navigate,
  formContext,
  user,
  t,
  setShowLaunchRecordOfDetentionDialog,
  setShowLaunchMedRecDialog,
  setShowLaunchApplicationDialog,
}: {
  navigate: NavigateFunction;
  t: any;
  formContext: FormContextData;
  user: ExtendedThalamosUser;
  setShowLaunchRecordOfDetentionDialog: (show: boolean) => void;
  setShowLaunchMedRecDialog: (show: boolean) => void;
  setShowLaunchApplicationDialog: (show: boolean) => void;
}) {
  if (!formContext.activeTeamworkWorkItem) return [];

  const {
    canLaunchRecordOfDetention,
    canLaunchApplication,
    canLaunchMedicalRecommendation,
    canRequestApplication,
    canRequestMedicalRecommendation,
    showDisabledLaunchMedRecButton,
    showDisabledLaunchApplicationButton,
    showDisabledRequestMedRecButton,
    showDisabledRequestApplicationButton,
    showDisabledLaunchRecordOfDetentionButton,
    amAmhpUser,
  } = admissionFormContextLogic(formContext, user);

  const elements: ReactElement[] = [];
  if (canLaunchRecordOfDetention || showDisabledLaunchRecordOfDetentionButton) {
    elements.push(
      <FormCategoryRow
        buttonTestId="start-new-form-button--record-of-detention"
        title={t("pages.formPage.formRowCategory.recordofDetentionTitle")}
        subtitle={`${t("pages.formPage.formRowCategory.h3Subtitle")} ${t(
          "pages.formPage.formRowCategory.multiSections",
        )}`}
        onLaunchForm={() => {
          setShowLaunchRecordOfDetentionDialog(true);
        }}
        launchFormLabel={t("pages.formPage.startForm")}
        disabled={showDisabledLaunchRecordOfDetentionButton}
      />,
    );
  }

  if (canLaunchMedicalRecommendation || showDisabledLaunchMedRecButton) {
    elements.push(
      ...[
        <FormCategoryRow
          buttonTestId="start-new-form-button--medical-recommendation"
          title={t("pages.formPage.formRowCategory.medicalRecommendationTitle")}
          subtitle={t("pages.formPage.formRowCategory.multiSections")}
          onLaunchForm={() => {
            setShowLaunchMedRecDialog(true);
          }}
          launchFormLabel={t("pages.formPage.startForm")}
          disabled={showDisabledLaunchMedRecButton}
          key={"medical-recommendation-launch"}
        />,
        <FormCategoryRow
          buttonTestId="no-form-button--medical-recommendation"
          title={"I will not make a medical recommendation"}
          subtitle={
            "Confirm if you choose not to make a medical recommendation"
          }
          onLaunchForm={() => {
            navigate(
              routeFns.formContextReasonForNotProceedingDialog(
                formContext.id,
                formContext.patientId,
              ),
            );
          }}
          launchFormLabel={"No recommendation"}
          disabled={showDisabledLaunchMedRecButton}
          key={"medical-recommendation-no-form"}
        />,
      ],
    );
  }

  if (canLaunchApplication || showDisabledLaunchApplicationButton) {
    elements.push(
      ...[
        <FormCategoryRow
          buttonTestId="start-new-form-button--application"
          title={t("pages.formPage.application")}
          subtitle={t("pages.formPage.formRowCategory.multiSections")}
          onLaunchForm={() => {
            setShowLaunchApplicationDialog(true);
          }}
          launchFormLabel={t("pages.formPage.startForm")}
          disabled={showDisabledLaunchApplicationButton}
          key={"application-launch"}
        />,
        <FormCategoryRow
          buttonTestId="no-form-button--application"
          title={"I will not make an application"}
          subtitle={"Confirm if you choose not to make an application"}
          onLaunchForm={() => {
            navigate(
              routeFns.formContextReasonForNotProceedingDialog(
                formContext.id,
                formContext.patientId,
              ),
            );
          }}
          launchFormLabel={"No application"}
          disabled={showDisabledLaunchApplicationButton}
          key={"application-no-form"}
        />,
      ],
    );
  }

  if (canRequestMedicalRecommendation || showDisabledRequestMedRecButton) {
    const s12FinderEnabled =
      user.sessionOrganisationConfiguration != null &&
      canAmhpSearchS12Doctors(user.sessionOrganisationConfiguration);

    // Only show the Dr Finder flow (Option 2) when the user is an AMHP
    // AND the Dr Finder feature flag is enabled.  Users in any other team type
    // should always see the standard "Request Medical Recommendation" flow
    // (Option 1), even if the flag is on for their organisation.
    const showDrFinderButton = s12FinderEnabled && amAmhpUser;

    elements.push(
      <FormCategoryRow
        buttonTestId="request-form-button--medical-recommendation"
        title={
          showDrFinderButton
            ? t("pages.formPage.formRowCategory.inviteDoctorTitle")
            : t(
                "pages.formPage.formRowCategory.requestMedicalRecommendationTitle",
              )
        }
        subtitle={
          showDrFinderButton
            ? t("pages.formPage.formRowCategory.inviteDoctorSubtitle")
            : t("pages.formPage.formRowCategory.multiSections")
        }
        onLaunchForm={() => {
          if (showDrFinderButton) {
            navigate(
              routeFns.formContextInterviews(
                formContext.id,
                formContext.patientId,
              ),
            );
          } else {
            navigate(
              routeFns.formContextPageSendRequest(
                formContext.id,
                formContext.patientId,
                { requestType: "medical-recommendation" },
              ),
            );
          }
        }}
        launchFormLabel={
          showDrFinderButton
            ? t("pages.formPage.formRowCategory.inviteDoctor")
            : t("pages.formPage.requestForm")
        }
        disabled={showDisabledRequestMedRecButton}
      />,
    );
  }

  if (canRequestApplication || showDisabledRequestApplicationButton) {
    elements.push(
      <>
        <FormCategoryRow
          buttonTestId="request-form-button--application"
          title={t("pages.formPage.formRowCategory.requestApplicationTitle")}
          subtitle={t("pages.formPage.formRowCategory.multiSections")}
          onLaunchForm={() => {
            navigate(
              routeFns.formContextPageSendRequest(
                formContext.id,
                formContext.patientId,
                { requestType: "application" },
              ),
            );
          }}
          launchFormLabel={t("pages.formPage.requestForm")}
          disabled={showDisabledRequestApplicationButton}
        />
      </>,
    );
  }

  return elements;
}

export function AdmissionsFormPage({
  formContext,
  reloadFormContext,
  user,
  patientTimeline,
  reloadPatientTimeline,
  onReconcileClick,
}: {
  formContext: FormContextData;
  reloadFormContext: () => void;
  user: ExtendedThalamosUser;
  patientTimeline: PatientTimelineResponse | null;
  reloadPatientTimeline: () => void;
  onReconcileClick?: () => void;
}) {
  const expiredAmendForm = formContext.forms.find(
    (f) => f.hasAmendExpired === true,
  );

  const nhsNumber = patientTimeline?.patient?.nhsNumber;

  const expiredAmendTemplate = getBaseFormTemplate(
    expiredAmendForm?.formTemplate?.id || "",
  )!;
  const expiredAmendSection = expiredAmendTemplate?.section;
  const isExpiredAmendOnSectionTwoOrThree =
    expiredAmendSection === "2" || expiredAmendSection === "3";

  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [pdfViewFormId, setPdfViewFormId] = useState<null | string>(null);
  const [
    showLaunchRecordOfDetentionDialog,
    setShowLaunchRecordOfDetentionDialog,
  ] = useState(false);
  const [showLaunchMedRecDialog, setShowLaunchMedRecDialog] = useState(false);
  const [showLaunchApplicationDialog, setShowLaunchApplicationDialog] =
    useState(false);

  const admissionData = admissionFormContextLogic(formContext, user);
  const {
    admissionHasCompletedApplication,
    inProgressForms,
    redoReplacementForms,
    previewableForms,
    usedForms,
    unusedForms,
    voidedForms,
    amAmhpUser,
    canScrutiniseAdmission,
    admissionNeedsScrutiny,
    canSendOrShareAdmission,
    signedPartOneRecordOfDetention,
    admissionHasCompletedRecordOfDetention,
    recordOfDetentionsInProgress,
    mayUploadFormsToExternalSystem,
    uploadableForms,
    isAdmissionClosed,
    isAdmissionArchivedOrClosed,
  } = admissionData;

  const newDraftId = useMemo(() => v4(), []);

  const [showUnusedForms, setShowUnusedForms] = useState(false);
  // Voided (superseded by a redo) forms are collapsed by default — kept for
  // audit/lineage, but de-emphasised away from the live workflow.
  const [showVoidedForms, setShowVoidedForms] = useState(false);

  const [showInProgressForms, setShowInProgressForms] = useState(true);
  const [showForms, setShowForms] = useState(true);
  const [showCompletedForms, setShowCompletedForms] = useState(true);

  const [showSelectFormsUploadDialog, setShowSelectFormsUploadDialog] =
    useState(false);
  const [showSelectFormsDownloadDialog, setShowSelectFormsDownloadDialog] =
    useState(false);

  // S12 Payment Request Review Dialog state
  const [showPaymentRequestReviewDialog, setShowPaymentRequestReviewDialog] =
    useState(false);

  // S12 feature flags
  const s12FeatureEnabled =
    user.sessionOrganisationConfiguration != null &&
    canAmhpSearchS12Doctors(user.sessionOrganisationConfiguration);
  // AMHP-facing payment-request visibility (review popup, banner and summary on
  // the admission page) is gated by "Approve Payment Requests" for the AMHP's
  // team — NOT by the doctor-side "Initiate payment requests" flag. Using the
  // doctor flag here previously hid the review popup whenever an org migrated to
  // the role-targeted settings (legacy `section12PaymentsEnabled` off), even
  // though the AMHP team could approve.
  const amhpCanApprovePayments =
    user.sessionOrganisationConfiguration != null &&
    canAmhpTeamApproveS12Payments(user.sessionOrganisationConfiguration);

  // Any doctor with a GMC number can submit a payment request from the form
  // context page. The payment flag only gates the automatic
  // popups after sign/no-med-rec, not the ability to create a request.
  // For guests, only those invited as a doctor (i.e. their work item on this
  // admission has requestType === "medical-recommendation") qualify — guests
  // invited as an AMHP, MHA office or ward team member must not see this.
  const canSubmitPaymentRequest =
    hasDoctorGmcNumber(user) || isGuestDoctorForFormContext(user, formContext);

  // Detect if user is an independent S12 doctor (for interview sections)
  const isS12DoctorWithFinder =
    isIndependentUserSession(user) && s12FeatureEnabled;

  // Get AMHP teams the user is associated with (from workItems with
  // accepted/completed status). Scope to the teams the user is actually a
  // member of: an accepted/completed work item belonging to *another* AMHP
  // team must not unlock payment-request visibility for this user. Without
  // this, once any AMHP team accepts the form context, the request would
  // become visible to every AMHP who can reach the page — including a
  // previously-invited AMHP who has since been replaced.
  const myTeamIds = useMemo(
    () =>
      new Set(
        user.memberships.filter(isExtendedTeamMembership).map((m) => m.teamId),
      ),
    [user.memberships],
  );
  const userAmhpTeamIds = useMemo(() => {
    return formContext.workItems
      .filter(
        (wi) =>
          wi.team.type === "amhp" &&
          myTeamIds.has(wi.teamId) &&
          (wi.status === "accepted" || wi.status === "completed"),
      )
      .map((wi) => wi.teamId);
  }, [formContext.workItems, myTeamIds]);

  // A guest AMHP is invited directly (not via an AMHP-team membership): their
  // recipient team is a guest team, so it never shows up in `userAmhpTeamIds`
  // (which requires team.type === "amhp"), and they are not inferred as an AMHP
  // user (their work item has no "application" requestType). Recognise them
  // straight from the guest-invite payment requests targeting a team they
  // belong to. The explicit invite alone enables review — the auto-created
  // guest org's "Approve Payment Requests" config does not gate it.
  const amGuest = isGuestUserSession(user);
  const guestAmhpInviteTeamIds = useMemo(() => {
    if (!amGuest) return new Set<string>();
    return new Set(
      (formContext.section12PaymentRequests ?? [])
        .filter((req) => req.isGuestAmhpInvite && myTeamIds.has(req.amhpTeamId))
        .map((req) => req.amhpTeamId),
    );
  }, [amGuest, formContext.section12PaymentRequests, myTeamIds]);

  const canReviewPayments =
    (amhpCanApprovePayments && amAmhpUser) || guestAmhpInviteTeamIds.size > 0;

  const reviewableAmhpTeamIds = useMemo(
    () => new Set([...userAmhpTeamIds, ...guestAmhpInviteTeamIds]),
    [userAmhpTeamIds, guestAmhpInviteTeamIds],
  );

  // Check for pending payment requests for the user's AMHP (or guest) teams
  const pendingPaymentRequestsForUser = useMemo(() => {
    if (!canReviewPayments) return [];
    const requests = formContext.section12PaymentRequests ?? [];
    return requests.filter(
      (req) =>
        req.status === "pending" && reviewableAmhpTeamIds.has(req.amhpTeamId),
    );
  }, [
    canReviewPayments,
    formContext.section12PaymentRequests,
    reviewableAmhpTeamIds,
  ]);

  // Get all payment requests for the user's AMHP (or guest) teams (for summary display)
  const paymentRequestsForAmhpTeams = useMemo(() => {
    if (!canReviewPayments) return [];
    const requests = formContext.section12PaymentRequests ?? [];
    return requests.filter((req) => reviewableAmhpTeamIds.has(req.amhpTeamId));
  }, [
    canReviewPayments,
    formContext.section12PaymentRequests,
    reviewableAmhpTeamIds,
  ]);

  // Requests billed to the team the user is currently operating as. Auto-open
  // is limited to these so a request for one of the user's other teams cannot
  // hijack the page on load; those stay reachable via the manual "Review
  // Requests" button. When the active team is unknown, fall back to all
  // reviewable requests.
  const pendingPaymentRequestsForActiveTeam = useMemo(() => {
    const activeTeamId = user.sessionContext?.teamId;
    if (activeTeamId == null) return pendingPaymentRequestsForUser;
    return pendingPaymentRequestsForUser.filter(
      (req) => req.amhpTeamId === activeTeamId,
    );
  }, [pendingPaymentRequestsForUser, user.sessionContext?.teamId]);

  // Auto-open the review dialog only for the active team's own requests
  useEffect(() => {
    if (pendingPaymentRequestsForActiveTeam.length > 0) {
      setShowPaymentRequestReviewDialog(true);
    }
  }, [pendingPaymentRequestsForActiveTeam.length]);

  const createDraftFn = async (formId: string) => {
    createDraftAndHandleResult(newDraftId, formId, navigate, formContext);
  };

  // Open the draft for a redo replacement. If the current user already has an
  // in-progress draft on the replacement (e.g. they started the redo, left, and
  // came back via "Continue redo"), resume it rather than creating a new one —
  // the backend rejects a second draft for the same form/user/team, which left
  // the user unable to get back into their draft (DEV-2415). Only when no such
  // draft exists (the freshly self-initiated / accepted replacement) do we
  // create one. Matches the backend dedup key (form + user + team).
  const startReplacementDraft = (formId: string) => {
    const replacement = formContext.forms.find((f) => f.id === formId);
    const existingDraftId = findResumableReplacementDraftId(
      replacement,
      user.id,
      user.sessionContext?.teamId,
    );
    if (existingDraftId) {
      navigate(
        routeFns.formDraftsComplete(existingDraftId, formContext.patientId),
      );
    } else {
      void createDraftFn(formId);
    }
  };

  // A chain redo creates a replacement form for the downstream author (e.g. the
  // AMHP's new application) server-side, with no draft started — so unlike the
  // self-initiate / accept paths there is no auto-opened draft. Proactively
  // prompt that author when they open the form context so they know to action it.
  const redoReplacementToStart = useMemo(
    () =>
      // redoReplacementForms (not inProgressForms) — the collapsed replacements
      // are filtered out of inProgressForms so they don't render their own row.
      // Prompt only the author who signs the replacement's NEXT part (see
      // userSignsNextReplacementPart): the lead doctor / AMHP for part 1, and —
      // for a joint med rec — the second author for part 2. Without this the
      // primary author would be pushed towards part 2 after signing part 1,
      // which is the second author's to sign.
      redoReplacementForms.find((f) => {
        const redoneFromForm = formContext.forms.find(
          (o) => o.id === f.redoneFromFormId,
        );
        const isOwnerOrTeam =
          f.ownerUserId === user.id ||
          (!!user.sessionContext?.teamId &&
            f.ownerTeamId === user.sessionContext.teamId);
        if (
          !userSignsNextReplacementPart(
            f,
            redoneFromForm,
            user.id,
            isOwnerOrTeam,
          )
        ) {
          return false;
        }
        // A joint med rec replacement retains the primary's signed part-1 draft,
        // so look for a draft owned by THIS user rather than any draft at all;
        // an application replacement is created with no drafts (chain redo).
        return formIsMedRec(f)
          ? (f.allDrafts ?? []).every((d) => d.ownerUserId !== user.id)
          : (f.allDrafts?.length ?? 0) === 0;
      }),
    [
      redoReplacementForms,
      formContext.forms,
      user.id,
      user.sessionContext?.teamId,
    ],
  );
  const [showRedoReplacementPrompt, setShowRedoReplacementPrompt] =
    useState(false);
  // Track the form context the prompt was last shown for, not a one-shot
  // boolean — otherwise, if this component stays mounted while navigating
  // between admissions, the chain-redo prompt would only ever appear once.
  const shownRedoPromptForContextRef = useRef<string | null>(null);
  useEffect(() => {
    // While the admission is read-only for redo, this prompt must not surface:
    // it would drop the author straight into the replacement draft, a redo
    // action they cannot complete here.
    if (
      !isAdmissionArchivedOrClosed &&
      redoReplacementToStart &&
      shownRedoPromptForContextRef.current !== formContext.id
    ) {
      shownRedoPromptForContextRef.current = formContext.id;
      setShowRedoReplacementPrompt(true);
    }
  }, [isAdmissionArchivedOrClosed, redoReplacementToStart, formContext.id]);
  const redoReplacementName = redoReplacementToStart
    ? getFullFormName(getBaseFormTemplate(redoReplacementToStart))
    : "";

  const showFinaliseConfirmation = () => {
    navigate(
      routeFns.formContextPageConfirmFinaliseDialog(
        formContext.id,
        formContext.patientId,
        {
          itemName: "admission",
        },
      ),
    );
  };

  const showMhaStatusConfirmation = () => {
    navigate(
      routeFns.formContextPageEditMhaStatusDialog(
        formContext.id,
        formContext.patientId,
        {
          itemName: "admission",
        },
      ),
    );
  };

  const showDeleteConfirmation = (draftFormId: UUID) => {
    navigate(
      routeFns.formContextPageConfirmDeleteDraftDialog(
        formContext.id,
        formContext.patientId,
        { draftFormId },
      ),
    );
  };

  const showRequestAmendDialog = (formId: UUID, formTemplateId: string) => {
    navigate(
      routeFns.formContextPageRequestAmendDialog(
        formContext.id,
        formContext.patientId,
        { formId, formTemplateId },
      ),
    );
  };

  const showRequestDeMinimisDialog = (formId: UUID, formTemplateId: string) => {
    navigate(
      routeFns.formContextPageRequestDeMinimisDialog(
        formContext.id,
        formContext.patientId,
        { formId, formTemplateId },
      ),
    );
  };

  const showRequestRedoDialog = (formId: UUID, formTemplateId: string) => {
    navigate(
      routeFns.formContextPageRequestRedoDialog(
        formContext.id,
        formContext.patientId,
        { formId, formTemplateId },
      ),
    );
  };

  const newFormElements = newForms({
    navigate,
    t,
    formContext,
    setShowLaunchRecordOfDetentionDialog,
    setShowLaunchMedRecDialog,
    setShowLaunchApplicationDialog,
    user: user,
  });

  // Get all admission forms data
  const formData = formContext.forms
    .map((f) => {
      return getLatestVersion(f)?.data[0]?.data;
    })
    .filter((data) => data);

  const isTeamWorkItemCompletedAndFormFinalised =
    formContext?.activeTeamworkWorkItem?.status === "completed" &&
    formContext.forms?.[0]?.status === "finalised";

  const currentUserIsAssigned =
    formContext.activeTeamworkWorkItem?.assignedUserId === user.id;

  const { isMobileView } = useScreenDetection();
  const showEditMhaStatus = userCanEditMhaStatus(user);

  const externalLinks = useExternalPatientLinks({
    patientId: formContext.patientId,
  });

  const uploadToRioExternalLink:
    ExternalPatientLinkEnhanced | null | undefined = useMemo(() => {
    if (mayUploadFormsToExternalSystem === false) {
      return null;
    }

    if (externalLinks.externalPatientLinksLoading === true) {
      return undefined;
    }

    const candidateLinks = (
      externalLinks.externalPatientLinks?.externalLinks ?? []
    ).filter(
      (externalPatientLink) => externalPatientLink.externalSystemType === "rio",
    );

    // The lowest number is the highest priority (i.e. 1 is higher priority than 2)
    const highestProirityLink = candidateLinks
      .filter((externalPatientLink) => externalPatientLink.canPush)
      .sort(
        (a, b) =>
          a.priorityWithinExternalSystem - b.priorityWithinExternalSystem,
      )?.[0];

    return highestProirityLink ?? null;
  }, [
    mayUploadFormsToExternalSystem,
    externalLinks.externalPatientLinks?.externalLinks,
    externalLinks.externalPatientLinksLoading,
  ]);

  // The picker and the button that skips past it are derived together here so
  // they cannot come to offer different forms.
  const formPicker = {
    forms: uploadableForms,
    onlyForm: uploadableForms.length === 1 ? uploadableForms[0] : undefined,
  };

  return (
    <Box
      data-testid="form-context:admission"
      data-formcontextid={formContext.id}
    >
      <externalLinks.Component />
      {showLaunchMedRecDialog && patientTimeline && (
        <NewFormDialog
          formTemplateFilterFn={
            user.sessionOrganisationConfiguration
              ?.mhaAdmissionsJointMedRecsEnabled
              ? formIsMedRec
              : formIsSingleMedRec
          }
          allowGuestToLaunchAnyForm={true}
          existingFormContext={formContext}
          allowRequestForm={false}
          title={t("pages.formPage.admissionStandardTitle")}
          patientId={formContext.patientId}
          closeDialog={() => {
            setShowLaunchMedRecDialog(false);
          }}
          patientTimeline={patientTimeline}
        />
      )}
      {showLaunchApplicationDialog && patientTimeline && (
        <NewFormDialog
          formTemplateFilterFn={formIsApplication}
          maxWidth="lg"
          allowGuestToLaunchAnyForm={true}
          existingFormContext={formContext}
          allowRequestForm={false}
          title={t("pages.formPage.applicationFormTitle")}
          patientId={formContext.patientId}
          closeDialog={() => {
            setShowLaunchApplicationDialog(false);
          }}
          patientTimeline={patientTimeline}
        />
      )}
      {showLaunchRecordOfDetentionDialog && patientTimeline && (
        // Assume we just link to application for now
        <NewFormDialog
          allowGuestToLaunchAnyForm={true}
          formTemplateFilterFn={formIsRecordOfDetention}
          maxWidth="lg"
          existingFormContext={formContext}
          allowRequestForm={false}
          title={t("pages.formPage.recordDetentionFormTitle")}
          patientId={formContext.patientId}
          dialogType={"record-of-detention"}
          closeDialog={() => {
            setShowLaunchRecordOfDetentionDialog(false);
          }}
          patientTimeline={patientTimeline}
        />
      )}
      {pdfViewFormId && (
        <VersionFormsPdfViewer
          inModal={true}
          forms={previewableForms}
          setFormId={setPdfViewFormId}
          formId={pdfViewFormId}
        />
      )}
      {!isAdmissionArchivedOrClosed &&
        showRedoReplacementPrompt &&
        redoReplacementToStart && (
          <PopupDialog
            open={true}
            onClose={() => {
              setShowRedoReplacementPrompt(false);
            }}
          >
            <PopupDialogTitle
              titleText="A form needs to be redone"
              closeDialog={() => {
                setShowRedoReplacementPrompt(false);
              }}
            />
            <Banner
              body={[
                `Because of a redo elsewhere in this assessment, ${redoReplacementName} needs to be completed again.`,
                "A new copy has been started for you — continue to fill it in and re-link the supporting forms.",
              ]}
              bannerType={BannerList.WARNING}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 3,
              }}
            >
              <Button
                label="Close"
                variant="outlined"
                onClick={() => {
                  setShowRedoReplacementPrompt(false);
                }}
              />
              <Button
                color="primary"
                label="Continue"
                onClick={() => {
                  setShowRedoReplacementPrompt(false);
                  void createDraftFn(redoReplacementToStart.id);
                }}
              />
            </Box>
          </PopupDialog>
        )}
      <Box sx={{ mb: 2 }}>
        {patientTimeline && (
          <PatientBanner
            patient={patientTimeline.patient}
            nhsNumber={nhsNumber ?? undefined}
            disableSticky
            isHorizontalLineHidden
            reloadPatientTimeline={reloadPatientTimeline}
            patientTimeline={patientTimeline}
            onReconcileClick={onReconcileClick}
          />
        )}
      </Box>
      {formData.length > 0 &&
        !admissionNeedsScrutiny &&
        !isTeamWorkItemCompletedAndFormFinalised && (
          <AdmissionPatientDemographicsOutdatedBanner
            patient={formContext.patient}
            formData={formData}
          />
        )}
      {recordOfDetentionsInProgress &&
        (() => {
          const recordOfDetentionPart = getLatestVersion(
            recordOfDetentionsInProgress,
          )?.data?.length;

          if (!recordOfDetentionPart) {
            return null;
          }

          const { formData, completedRecordOfDetention } =
            admissionFormContextLogic(formContext, user);

          const { draftIdOnNextPart, isAmend, myFormWorkItem, canLaunchDraft } =
            formData.get(recordOfDetentionsInProgress?.id) ?? {};

          const canUserContinueForm = canContinueAdmissionForm(
            draftIdOnNextPart!,
            currentUserIsAssigned,
            completedRecordOfDetention,
            isAmend,
          );

          const canUserStartForm = canStartAdmissionForm(
            canLaunchDraft,
            myFormWorkItem,
            currentUserIsAssigned,
          );

          if (canUserContinueForm || canUserStartForm) {
            return (
              <RecordOfDetentionBanner
                recordOfDetentionsInProgress={recordOfDetentionsInProgress}
                formContext={formContext}
                createDraftFn={createDraftFn}
              />
            );
          }

          return null;
        })()}
      <Section4Banners formContext={formContext} />
      {/* S12 Payment Request Banner for AMHP - only shown when there are PENDING requests */}
      {canReviewPayments && pendingPaymentRequestsForUser.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Banner
            title="S12 Doctor Payment Requests"
            body={[
              `You have ${pendingPaymentRequestsForUser.length} pending payment request${pendingPaymentRequestsForUser.length > 1 ? "s" : ""} to review.`,
            ]}
            bannerType={BannerList.INFO}
            rightButtonLabel="Review Requests"
            rightButtonTestId="review-payment-requests-button"
            onRightButton={() => {
              setShowPaymentRequestReviewDialog(true);
            }}
          />
        </Box>
      )}
      {/* Doctor's Interviews Section — shows interviews they are a participant on */}
      {isS12DoctorWithFinder && (
        <DoctorInterviewsSection formContextId={formContext.id} />
      )}
      {/* S12 Payment Request Review Dialog */}
      {showPaymentRequestReviewDialog && (
        <AmhpPaymentRequestReviewPopup
          formContext={formContext}
          onClose={() => {
            setShowPaymentRequestReviewDialog(false);
          }}
          reloadFormContext={reloadFormContext}
        />
      )}
      {expiredAmendSection === "4" && (
        <Box sx={{ mb: 2 }}>
          <Banner
            title={t("pages.admissionFormPage.bannerTitle")}
            rightButtonTestId="amend-period-expired"
            body={[t("pages.admissionFormPage.amendPeriodExpired72Hours")]}
            bannerType={BannerList.WARNING}
          />
        </Box>
      )}
      {isExpiredAmendOnSectionTwoOrThree && (
        <Box sx={{ mb: 2 }}>
          <Banner
            title={t("pages.admissionFormPage.bannerTitle")}
            rightButtonTestId="amend-period-expired"
            body={[t("pages.admissionFormPage.amendPeriodExpired14Day")]}
            bannerType={BannerList.WARNING}
          />
        </Box>
      )}
      {canScrutiniseAdmission && (
        <Box sx={{ mb: 2 }}>
          <Banner
            title={t("pages.admissionFormPage.scrutiniseAdmissionBannerTitle")}
            rightButtonLabel={t("buttonLabels.complete")}
            rightButtonTestId="mark-scrutiny-complete-button"
            onRightButton={
              showEditMhaStatus
                ? showMhaStatusConfirmation
                : showFinaliseConfirmation
            }
            bannerType={BannerList.INFO}
          />
        </Box>
      )}
      <WorkItemStatusCard
        formContext={formContext}
        reloadFormContext={reloadFormContext}
        user={user}
        amAmhpUser={amAmhpUser}
      />
      {newFormElements.length > 0 && (
        <Container addMarginBottom>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
            }}
          >
            <FormTitle
              subtitleText={t("common.newAdmissionForm")}
              hasContainerMarginBottom={false}
              subTitleTextFontSize={2}
            />
            <ExpandMore
              role="button"
              isExpanded={showForms}
              onClick={() => {
                setShowForms(!showForms);
              }}
              aria-expanded={showForms}
              aria-label={t("components.cardExtended.expandMoreLabel")}
            >
              <ExpandMoreIcon sx={{ color: "primary.main" }} />
            </ExpandMore>
          </Box>
          {showForms && (
            <Box sx={{ mt: 1 }}>
              <>{newFormElements}</>
            </Box>
          )}
        </Container>
      )}
      {/* In-progress (draft) form rows */}
      {!!inProgressForms.length && (
        <Container addMarginBottom>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
            }}
          >
            <FormTitle
              subtitleText={`In-progress forms (${inProgressForms.length})`}
              hasContainerMarginBottom={false}
              subTitleTextFontSize={2}
            />
            <ExpandMore
              role="button"
              isExpanded={showInProgressForms}
              onClick={() => {
                setShowInProgressForms(!showInProgressForms);
              }}
              aria-expanded={showInProgressForms}
              aria-label={t("components.cardExtended.expandMoreLabel")}
            >
              <ExpandMoreIcon sx={{ color: "primary.main" }} />
            </ExpandMore>
          </Box>
          {showInProgressForms && (
            <>
              {!!inProgressForms.length && (
                <Box sx={{ mt: theme.spacing(2) }}>
                  {inProgressForms.map((f) => {
                    const merged = findFormsMerged(
                      patientTimeline?.mergedPatientData,
                      formContext,
                      f,
                    );
                    return (
                      <AdmissionFormRow
                        key={f.id}
                        setPdfViewFormId={setPdfViewFormId}
                        form={f}
                        formContext={formContext}
                        createDraftFn={() => createDraftFn(f.id)}
                        showDeleteConfirmation={showDeleteConfirmation}
                        user={user}
                        reloadFormContext={reloadFormContext}
                        lastMerged={merged?.lastMerged}
                        patientTimeline={patientTimeline}
                      />
                    );
                  })}
                </Box>
              )}
            </>
          )}
        </Container>
      )}
      {/* "Used" completed form rows */}
      {!!usedForms.length && (
        <Container addMarginBottom>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
            }}
          >
            <FormTitle
              subtitleText={`Completed forms (${usedForms.length})`}
              hasContainerMarginBottom={false}
              subTitleTextFontSize={2}
            />
            {admissionHasCompletedApplication &&
              signedPartOneRecordOfDetention && (
                <Button
                  variant={"outlined"}
                  color={"primary"}
                  label="Download All"
                  endIcon={ButtonIcon.download}
                  sx={{ ml: 2, height: 40 }}
                  onClick={() => {
                    setShowSelectFormsDownloadDialog(true);
                  }}
                />
              )}
            {mayUploadFormsToExternalSystem && uploadToRioExternalLink && (
              <Button
                variant={"outlined"}
                color={"primary"}
                label="Upload all to Rio"
                endIcon={ButtonIcon.rioUpload}
                sx={{ ml: 2, height: 40 }}
                onClick={() => {
                  const onUpload = async () => {
                    if (formPicker.onlyForm !== undefined) {
                      await externalLinks.uploadFormPdfViaExternalPatientLink(
                        uploadToRioExternalLink,
                        [formPicker.onlyForm.id],
                        formContext,
                      );
                    } else {
                      setShowSelectFormsUploadDialog(true);
                    }
                  };
                  void onUpload();
                }}
              />
            )}
            {((showSelectFormsUploadDialog && uploadToRioExternalLink) ||
              showSelectFormsDownloadDialog) && (
              <SelectFormsDialog
                open={
                  showSelectFormsUploadDialog || showSelectFormsDownloadDialog
                }
                onClose={() => {
                  setShowSelectFormsUploadDialog(false);
                  setShowSelectFormsDownloadDialog(false);
                }}
                forms={formPicker.forms}
                formContext={formContext}
                uploadFormPdfViaExternalPatientLink={
                  externalLinks.uploadFormPdfViaExternalPatientLink
                }
                uploadToRioExternalLink={uploadToRioExternalLink}
                mode={showSelectFormsUploadDialog ? "upload" : "download"}
              />
            )}
            <ExpandMore
              role="button"
              isExpanded={showCompletedForms}
              onClick={() => {
                setShowCompletedForms(!showCompletedForms);
              }}
              aria-expanded={showCompletedForms}
              aria-label={t("components.cardExtended.expandMoreLabel")}
            >
              <ExpandMoreIcon sx={{ color: "primary.main" }} />
            </ExpandMore>
          </Box>

          {showCompletedForms && !!usedForms.length && (
            <Box sx={{ mt: theme.spacing(2) }}>
              {usedForms.map((f) => {
                const merged = findFormsMerged(
                  patientTimeline?.mergedPatientData,
                  formContext,
                  f,
                );
                return (
                  <AdmissionFormRow
                    key={f.id}
                    setPdfViewFormId={setPdfViewFormId}
                    form={f}
                    formContext={formContext}
                    user={user}
                    showRequestAmendDialog={showRequestAmendDialog}
                    showRequestDeMinimisDialog={showRequestDeMinimisDialog}
                    showRequestRedoDialog={showRequestRedoDialog}
                    startReplacementDraft={startReplacementDraft}
                    reloadFormContext={reloadFormContext}
                    createDraftFn={() => createDraftFn(f.id)}
                    lastMerged={merged?.lastMerged}
                    patientTimeline={patientTimeline}
                  />
                );
              })}
            </Box>
          )}
        </Container>
      )}
      {/* "Unused" completed form rows */}
      {unusedForms.length > 0 && (
        <Box sx={{ mt: theme.spacing(2) }}>
          <Container addMarginBottom>
            <FormTitle
              subtitleText={`Unused forms (${unusedForms.length})`}
              hasContainerMarginBottom={false}
              subTitleTextFontSize={2}
            />
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Switch
                  checked={!showUnusedForms}
                  onClick={() => {
                    setShowUnusedForms(!showUnusedForms);
                  }}
                />
              }
              label={"Hide forms"}
            />
            {showUnusedForms && (
              <Box sx={{ mt: theme.spacing(2) }}>
                {unusedForms.map((f) => {
                  const merged = findFormsMerged(
                    patientTimeline?.mergedPatientData,
                    formContext,
                    f,
                  );
                  return (
                    <AdmissionFormRow
                      key={f.id}
                      setPdfViewFormId={setPdfViewFormId}
                      form={f}
                      formContext={formContext}
                      user={user}
                      createDraftFn={() => createDraftFn(f.id)}
                      lastMerged={merged?.lastMerged}
                      showRequestAmendDialog={showRequestAmendDialog}
                      showRequestDeMinimisDialog={showRequestDeMinimisDialog}
                      showRequestRedoDialog={showRequestRedoDialog}
                      startReplacementDraft={startReplacementDraft}
                      reloadFormContext={reloadFormContext}
                      patientTimeline={patientTimeline}
                    />
                  );
                })}
              </Box>
            )}
          </Container>
        </Box>
      )}
      {/* "Voided" forms — superseded by a redo. Kept for audit/lineage only,
          collapsed by default, and never part of the live workflow. */}
      {voidedForms.length > 0 && (
        <Box sx={{ mt: theme.spacing(2) }}>
          <Container addMarginBottom>
            <FormTitle
              subtitleText={`Voided forms (${String(voidedForms.length)})`}
              hasContainerMarginBottom={false}
              subTitleTextFontSize={2}
            />
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Switch
                  checked={!showVoidedForms}
                  onClick={() => {
                    setShowVoidedForms(!showVoidedForms);
                  }}
                />
              }
              label={"Hide forms"}
            />
            {showVoidedForms && (
              <Box sx={{ mt: theme.spacing(2) }}>
                {voidedForms.map((f) => {
                  const merged = findFormsMerged(
                    patientTimeline?.mergedPatientData,
                    formContext,
                    f,
                  );
                  return (
                    // No request dialogs passed: a voided form is terminal, so
                    // it only offers View PDF / Download.
                    <AdmissionFormRow
                      key={f.id}
                      setPdfViewFormId={setPdfViewFormId}
                      form={f}
                      formContext={formContext}
                      user={user}
                      createDraftFn={() => createDraftFn(f.id)}
                      lastMerged={merged?.lastMerged}
                      reloadFormContext={reloadFormContext}
                      patientTimeline={patientTimeline}
                    />
                  );
                })}
              </Box>
            )}
          </Container>
        </Box>
      )}
      {/* S12 Payment Requests Section for Doctor */}
      {canSubmitPaymentRequest && (
        <DoctorPaymentRequestsSection
          formContext={formContext}
          user={user}
          reloadFormContext={reloadFormContext}
        />
      )}
      <ParticipantsList formContext={formContext} />
      {/* Independent Doctors Section — AMHP users with S12 feature enabled */}
      {amAmhpUser && s12FeatureEnabled && (
        <InterviewsSection
          formContextId={formContext.id}
          patientId={formContext.patientId}
          user={user}
          reloadFormContext={reloadFormContext}
          hasActiveWorkItem={
            !hasNoActiveWorkItemOrRejectedCancelledOrCompleted(formContext) &&
            currentUserIsAssigned
          }
        />
      )}
      {/* Payment Request Summary for AMHP - always shown when there are requests for their teams */}
      {canReviewPayments && paymentRequestsForAmhpTeams.length > 0 && (
        <PaymentRequestSummary
          requests={paymentRequestsForAmhpTeams}
          onReviewClick={
            pendingPaymentRequestsForUser.length > 0
              ? () => {
                  setShowPaymentRequestReviewDialog(true);
                }
              : undefined
          }
        />
      )}
      <FormFooterSection
        hideSubmit={!canSendOrShareAdmission}
        {...(user.sessionOrganisationConfiguration?.uploadFormsEnabled &&
          !isAdmissionClosed &&
          !admissionHasCompletedRecordOfDetention &&
          !hasNoActiveWorkItemOrRejectedOrCancelled(formContext) && {
            secondaryLeftButton: (
              <Button
                fullWidth={isMobileView}
                variant="outlined"
                sx={{ backgroundColor: "white" }}
                label={"Upload form"}
                onClick={() => {
                  navigate(
                    routeFns.formUploadSelectedTemplate(
                      formContext.id,
                      formContext.patientId,
                    ),
                  );
                }}
              />
            ),
          })}
        isSticky={true}
        onSave={() => {
          navigate(
            routeFns.formContextPageSendRequest(
              formContext.id,
              formContext.patientId,
              {},
            ),
          );
        }}
        saveLabel={t("buttonLabels.send")}
        discardLabel={t("buttonLabels.activityLog")}
        onCancel={() => {
          navigate(
            routeFns.formContextPageActivityLog(
              formContext.id,
              formContext.patientId,
            ),
          );
        }}
      />
    </Box>
  );
}

function RecordOfDetentionBanner({
  recordOfDetentionsInProgress,
  formContext,
  createDraftFn,
}: {
  recordOfDetentionsInProgress: FormContextMiniForm;
  formContext: FormContextData;
  createDraftFn: (id: string) => Promise<void>;
}) {
  const userContext = useContext(LoggedInUserContext);
  const user = userContext?.user;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const recordOfDetentionTemplate = getBaseFormTemplate(
    recordOfDetentionsInProgress?.formTemplate?.id || "",
  )!;

  const recordOfDentionFullFormName = getFullFormName(
    recordOfDetentionTemplate,
  );

  const activeWorkItemForm = formContext?.activeTeamworkWorkItem?.forms.find(
    (f) => f.id === recordOfDetentionsInProgress?.id,
  );

  const workItemDraftId = activeWorkItemForm?.formDraftId;

  return (
    <Box sx={{ mb: 2 }}>
      <Banner
        bannerType={BannerList.INFO}
        title={`Continue ${recordOfDentionFullFormName}`}
        disabledButtonRight={
          formContext?.activeTeamworkWorkItem?.assignedUserId !== user?.id
        }
        onRightButton={() => {
          workItemDraftId
            ? navigate(
                routeFns.formDraftsComplete(
                  workItemDraftId,
                  formContext.patientId,
                ),
              )
            : createDraftFn(recordOfDetentionsInProgress.id);
        }}
        rightButtonLabel={t("buttonLabels.continue")}
      />
    </Box>
  );
}

/**
 * Payment request summary component for AMHPs
 */
function PaymentRequestSummary({
  requests,
  onReviewClick,
}: {
  requests: Section12PaymentRequestSummary[];
  onReviewClick?: () => void;
}) {
  const getStatusChip = (status: Section12PaymentRequestSummary["status"]) => (
    <Chip
      label={STATUS_LABELS[status]}
      color={STATUS_COLORS[status]}
      sx={STATUS_CHIP_SX[status]}
      size="small"
    />
  );

  const pendingCount = requests.filter((r) => r.status === "pending").length;

  return (
    <Container addMarginBottom>
      <Box sx={{ mb: 2 }}>
        <FormTitle
          subtitleText={`Payment Requests (${requests.length})`}
          hasContainerMarginBottom={false}
          subTitleTextFontSize={2}
        />
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {requests.map((request) => (
          <Box
            key={request.id}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              backgroundColor: "background.paper",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Box sx={{ fontWeight: 500 }}>
                {request.doctorName
                  ? formatDoctorDisplayName(
                      request.doctorName,
                      request.doctorTeamName,
                      request.doctorTeamType,
                    )
                  : "Unknown Doctor"}
              </Box>
              <Box sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                Submitted:{" "}
                {new Date(request.created).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </Box>
              <Box sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                Reference: {request.paymentReference}
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {getStatusChip(request.status)}
            </Box>
          </Box>
        ))}
      </Box>
      {onReviewClick && pendingCount > 0 && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            label={`Review ${pendingCount} Pending Request${pendingCount > 1 ? "s" : ""}`}
            onClick={onReviewClick}
          />
        </Box>
      )}
    </Container>
  );
}
